import YCB from 'ycb';

import apiConfig from './configs/api';
import appConfig, { Feature } from './configs/app';
import { tinder } from './configs/constants/domain';
import experiencesConfig from './configs/experiences';
import loginPageConfig from './configs/login';
import navConfig from './configs/nav';
import routesConfig, { Route } from './configs/routes';
import servicesConfig from './configs/services';
import staticSite from './configs/static';
import stickCenters from './configs/stickerCenters';
import dimensions from './dimensions';
import { Config } from './types';
import configUtils from './utils';

let config: Config;

const DEFAULT_YCB_OBJ = new YCB.Ycb(
  [dimensions as Record<string, any>].concat(
    apiConfig,
    appConfig,
    experiencesConfig,
    loginPageConfig,
    navConfig,
    routesConfig,
    servicesConfig,
    staticSite,
    stickCenters
  )
);

export function getConfig(
  context?: Record<string, any>,
  ycbObj = DEFAULT_YCB_OBJ
): Readonly<Config> {
  config = ycbObj.read(configUtils.getContext(context));
  return config;
}

export function getRouteFromPath(path: string): string {
  const routePrefix = config.routes.prefix;
  if (path.slice(0, routePrefix.length) === routePrefix) {
    return path;
  }
  return `${routePrefix}${path}`;
}

type PathConfig = {
  withPrefix?: boolean;
  removeRegex?: boolean;
  withHost?: boolean;
};

const defaultPathConfig: PathConfig = {
  withPrefix: true,
  removeRegex: true,
};

export type GetRouteOptions = {
  paramValues?: Record<string, string>;
  pathConfig?: PathConfig;
  queryParams?: Record<string, string>;
};

/**
 * @description
 * This returns route url based on route key.
 * Also it adds:
 * - Param values to previously configured urls.
 * - Query params
 * - Has path config flags
 * @param routeObj
 * @returns route url
 */
export function getRoute(route: Route, opts?: GetRouteOptions): string {
  const params = opts?.paramValues;
  const pathConfig = opts?.pathConfig ?? defaultPathConfig;
  const routeBody = config.routes[route];

  const routePrefix = config.routes.prefix;
  const { withPrefix, removeRegex, withHost } = {
    ...defaultPathConfig,
    ...pathConfig,
  };

  let routeWithPrefix = withPrefix ? `${routePrefix}${routeBody}` : routeBody;

  const isRoot = routeWithPrefix.length === routePrefix.length + 1;
  const isPrefix = routePrefix === routeBody;

  if (isRoot || isPrefix) return routeBody;

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      routeWithPrefix = routeWithPrefix.replace(`:${paramKey}`, paramValue);
    });
  }
  if (removeRegex) {
    routeWithPrefix = routeWithPrefix.replace(/\(.*?\)/g, '');
    routeWithPrefix = routeWithPrefix.replace(/\?/g, '');
  }

  if (opts?.queryParams) {
    Object.entries(opts?.queryParams).forEach(([paramKey, paramValue], index) => {
      const keyValue = `${paramKey}=${paramValue}`;
      if (index === 0) {
        routeWithPrefix = `${routeWithPrefix}?${keyValue}`;
      } else {
        routeWithPrefix = `${routeWithPrefix}&${keyValue}`;
      }
    });
  }

  return withHost ? `${tinder}${routeWithPrefix}` : routeWithPrefix;
}

const overrides: Partial<Record<Feature, boolean>> = {};
export function setEnabled(feature: Feature, setting: boolean) {
  overrides[feature] = setting;
}

export function isEnabled(feature: Feature): boolean {
  if (Object.prototype.hasOwnProperty.call(overrides, feature)) {
    // @ts-expect-error: inference can't detect that overrides[feature] will be defined
    return overrides[feature];
  }

  return !!config.features[feature];
}

export const getConfigWithContext = (context: Record<string, any> = {}) => getConfig(context);

export function isRouteInList(currentRoute: string = '/', routeList: string[]): boolean {
  return routeList.some((routeItem) => currentRoute.includes(routeItem));
}

export default getConfig();
